import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Embers } from "@/components/site/Embers";

const IMG = "https://static.prod-images.emergentagent.com/jobs/55e8a073-484f-45a0-9ce0-724193ee87a9/images/";
const NATARAJA = IMG + "57f12d36b81074e7c92028ed57169c05a2784e9357f7cfca51140a26eab8dc0f.jpeg";
const CREATION = IMG + "3734763d436ea761c974faeb73838c5395d4e6cef5b936ae4140fba52b37fc2d.jpeg";
const DANCER = IMG + "50224b2b7050c9b5b7f64171c4cf786da7ab46cefb77efae40ccb4bb05349139.jpeg";

function Mandala() {
  return (
    <svg viewBox="0 0 400 400" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[120vh] w-[120vh] opacity-[0.16] animate-[spin_120s_linear_infinite]" aria-hidden>
      {[190, 160, 130, 100].map((r, i) => (
        <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="#D4AF37" strokeWidth={i % 2 ? 0.6 : 1} strokeDasharray={i % 2 ? "2 6" : "14 4"} />
      ))}
      {Array.from({ length: 24 }, (_, i) => (
        <ellipse key={i} cx="200" cy="55" rx="7" ry="22" fill="none" stroke="#E06D3B" strokeWidth="0.7" transform={`rotate(${i * 15} 200 200)`} />
      ))}
    </svg>
  );
}

function SideScript({ side, text }) {
  return (
    <p className={`hidden md:block absolute top-1/2 -translate-y-1/2 ${side === "left" ? "left-8" : "right-8"} font-display text-xs tracking-[0.7em] uppercase text-[#D4AF37]/80`} style={{ writingMode: "vertical-rl", transform: side === "left" ? "translateY(-50%) rotate(180deg)" : undefined }}>
      {text}
    </p>
  );
}

function Arch({ src, alt, o, s, origin = "50% 50%", testid }) {
  return (
    <motion.div style={{ opacity: o }} className="absolute inset-0 grid place-items-center" data-testid={testid}>
      <div className="relative h-[78vh] max-w-[86vw] aspect-[2/3]">
        <div className="absolute -inset-3 rounded-t-full border border-[#D4AF37]/40" />
        <div className="absolute -inset-6 rounded-t-full border border-[#D4AF37]/15" />
        <div className="relative h-full w-full overflow-hidden rounded-t-full shadow-[0_0_120px_-10px_rgba(224,109,59,0.45)]">
          <motion.img src={src} alt={alt} style={{ scale: s, transformOrigin: origin }} className="h-full w-full object-cover" />
          <div className="absolute inset-0 shadow-[inset_0_0_80px_20px_#0B0907]" />
        </div>
      </div>
    </motion.div>
  );
}

function Chapter({ o, num, color = "#D4AF37", children, className }) {
  return (
    <motion.div style={{ opacity: o }} className={`absolute px-6 drop-shadow-[0_2px_14px_rgba(0,0,0,1)] ${className}`}>
      <p className="text-[11px] uppercase tracking-[0.5em]" style={{ color }}>Chapter {num}</p>
      <p className="font-display italic text-3xl sm:text-5xl text-[#F5EFE6] mt-3 leading-tight">{children}</p>
    </motion.div>
  );
}

export function ScrollStory() {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const natO = useTransform(p, [0, 0.2, 0.3], [1, 1, 0]);
  const natS = useTransform(p, [0, 0.3], [1, 1.12]);
  const eyeO = useTransform(p, [0.22, 0.32, 0.44, 0.52], [0, 1, 1, 0]);
  const eyeS = useTransform(p, [0.22, 0.52], [1, 1.15]);
  const danO = useTransform(p, [0.44, 0.52, 0.8, 0.92], [0, 1, 1, 0.4]);
  const danS = useTransform(p, [0.46, 0.78], [3.4, 1]);
  const c1 = useTransform(p, [0, 0.12, 0.2], [1, 1, 0]);
  const c2 = useTransform(p, [0.3, 0.36, 0.44, 0.48], [0, 1, 1, 0]);
  const c3 = useTransform(p, [0.62, 0.68, 0.76, 0.8], [0, 1, 1, 0]);
  const titleO = useTransform(p, [0.8, 0.92], [0, 1]);
  const titleY = useTransform(p, [0.8, 0.95], [60, 0]);
  const hintO = useTransform(p, [0, 0.06], [1, 0]);

  return (
    <section ref={ref} className="relative h-[420vh]" data-testid="scroll-story">
      <div className="sticky top-0 h-screen overflow-hidden bg-[#0B0907]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_55%,#5a1414_0%,#2a0d0a_35%,#0B0907_75%)]" />
        <Mandala />
        <SideScript side="left" text="Tandava · Abhinaya · Nritta" />
        <SideScript side="right" text="Mysore · Banaras · Kanchipuram" />
        <Arch src={NATARAJA} alt="Nataraja in the cosmic tandava" o={natO} s={natS} origin="50% 45%" testid="story-nataraja" />
        <Arch src={CREATION} alt="Ancient temple reliefs of the karana dance poses" o={eyeO} s={eyeS} origin="50% 60%" testid="story-creation" />
        <Arch src={DANCER} alt="Classical dancer in full pose" o={danO} s={danS} origin="52% 20%" testid="story-dancer" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,#0B0907_100%)]" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0B0907] to-transparent" />
        <Embers />

        <Chapter o={c1} num="I" className="inset-x-0 bottom-24 text-center">In the beginning, there was the dance.</Chapter>
        <Chapter o={c2} num="II" color="#E06D3B" className="inset-x-0 bottom-20 text-center">
          Then there was the creation.
          <span className="block font-serif-it not-italic text-base sm:text-xl text-[#F5EFE6]/75 mt-3 max-w-2xl mx-auto">Sage Bharata wrote the Natya Shastra, and India carved its first dance into stone.</span>
        </Chapter>
        <Chapter o={c3} num="III" className="left-0 md:left-10 bottom-24 max-w-md">Now, the whole of India moves with her tale.</Chapter>

        <motion.div style={{ opacity: titleO, y: titleY }} className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="text-[11px] uppercase tracking-[0.6em] text-[#D4AF37] mb-6">Ancient Origin</p>
          <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight max-w-6xl drop-shadow-[0_6px_30px_rgba(0,0,0,0.9)]" data-testid="hero-title">
            Tales of Indian <span className="italic font-medium gold-text">heritage,</span><br /> redefined.
          </h1>
          <p className="font-serif-it text-xl sm:text-2xl text-[#F5EFE6]/80 mt-6" data-testid="hero-subtitle">Sarees that speak for their own.</p>
          <Link to="/store" className="btn-glow mt-10 inline-flex items-center gap-3 rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-[#0B0907] hover:bg-[#F3E5AB]" data-testid="explore-store-button">
            Explore our store <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>

        <motion.div style={{ opacity: hintO }} className="absolute bottom-6 left-6 md:left-0 md:inset-x-0 md:text-center text-[10px] uppercase tracking-[0.4em] text-[#A89F91]">
          Scroll to awaken
        </motion.div>
      </div>
    </section>
  );
}
